import type React from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";

import Icon from "@/base/components/Global/Icon";
import { getImageFromAPI } from "@/base/utils/imageHelper";
import config from "@/constants/config";
import { getBlobStorage, getBrandTheme } from "@/theme";

const useStyles = createUseStyles(({ spacing, font, color }) => ({
  extrasRow: {
    background: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
  },
  row: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    fontWeight: font.weight.m,
    fontSize: font.size.s,
    lineHeight: font.lineHeight.s,
    color: color.productPageDescriptionText,
    padding: [spacing.l, 0],
  },
  headerBlock: {
    display: "flex",
    flexDirection: "column",
  },
  price: {
    minWidth: 60,
    marginLeft: "auto",
  },
  header: {
    fontSize: 32,
    fontWeight: font.weight.s,
    lineHeight: "34px",
    color: color.productPageHeadingText,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    marginRight: spacing.s,
  },
  itemInfoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  imgContainer: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.l,
    alignItems: "center",
    padding: [spacing.s, spacing.l],
    position: "relative",
  },
  img: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
  },
  imgTitle: {
    fontSize: font.size.s,
    fontWeight: font.weight.m,
    lineHeight: font.lineHeight.xxs,
    color: color.productPageImageTitle,
    textAlign: "center",
  },
  imgBlock: {
    display: "flex",
    flexWrap: "wrap",
    gap: spacing.xxl,
    justifyContent: "center",
  },
  mandatoryOption: {
    fontWeight: font.weight.m,
    fontSize: font.size.s,
    lineHeight: font.lineHeight.xxs,
    color: color.productPageMandatoryOption,
    paddingTop: spacing.l,
  },
  error: {
    color: color.error,
  },
  checkedIcon: {
    position: "absolute",
    top: -5,
    right: 10,
  },
  extrasBox: {
    gap: 24,
  },
}));

type ExtrasImgRowProps = {
  name: string;
  price: number;
  isAdded: boolean;
  isMandatory: boolean;
  img?: string;
  hasDefaultSelected?: boolean;
};

export const ExtrasImgRow: React.FC<ExtrasImgRowProps> = ({
  name,
  price,
  isAdded,
  img,
  hasDefaultSelected = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("productPage");

  const { extraChoiceIcon } = getBlobStorage();
  const { opacity } = getBrandTheme();

  return (
    <div className={classes.imgContainer}>
      {isAdded && (
        <Icon
          url={extraChoiceIcon}
          width={24}
          height={24}
          className={classes.checkedIcon}
        />
      )}
      <div
        className={classes.img}
        style={{
          backgroundImage: `url(${getImageFromAPI(img || "")})`,
          opacity: hasDefaultSelected
            ? isAdded
              ? opacity.enabled
              : opacity.disabled
            : opacity.enabled,
        }}
      />
      <div className={classes.imgTitle}>
        <div>{name}</div>
        {price && price > 0 ? (
          <div className={classes.price}>
            {t("ExtrasImgRow.price", {
              price,
              formatParams: {
                price: {
                  currency: config.appConfig.currency.currencyCode,
                  locale: config.appConfig.currency.formatLocale,
                },
              },
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};
